<script lang="ts" setup>
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'
import type { SectionWithIndex } from '~/types'

const toast = useToast()
interface Props extends Pick<SectionWithIndex, 'collection' | 'index' | 'id' | 'title' | 'theme' | 'subtitle' | 'descriptionAdditional' | 'expand' | 'buttons' | 'description'> { }
const props = defineProps<Props>()

type Schema = z.output<typeof schema>

const schema = z.object({
  name: z.string().min(3, 'Name is required'),
  email: z.string().email('Please enter a valid email address'),
  message: z.string().min(10, 'Please enter a message (min 10 chars)'),
})

const msg = reactive({
  name: '',
  email: '',
  message: '',
})

const status = ref<'idle' | 'loading' | 'success' | 'error'>('idle')
const buttonMessage = computed(() => {
  if (status.value === 'loading') {
    return 'Sending...'
  }
  if (status.value === 'success') {
    return 'Sent!'
  }
  if (status.value === 'error') {
    return 'Error!'
  }
  return 'Send Message'
})
const pb = useNuxtApp().$pb
async function sendMessage(event: FormSubmitEvent<Schema>) {
  status.value = 'loading'
  try {
    await pb.send(`/api/send-message`, { method: 'POST', body: event.data })
    status.value = 'success'
  }
  catch {
    setMessageError()
  }
}

function setMessageError() {
  status.value = 'error'
  toast.add({
    title: 'Error',
    description: 'There was an error sending your message. Please try again later.',

  })
  setTimeout(() => {
    status.value = 'idle'
  }, 3000)
}

const input = {
  base: '!bg-darkblue !text-white !ring-transparent !ring-2 !p-4 !rounded-2xl !border-0 !focus:ring-blue placeholder:text-white !text-xl',
}
const label = {
  label: { base: '!text-white' },
  error: '!text-white/80 text-right',
}
</script>

<template>
  <div
    class="mx-auto max-w-screen-2xl w-full px-6 relative z-20 mt-32 lg:mt-40"
    :class="[theme, index === 0 ? 'pt-32 lg:pt-64' : '']"
  >
    <HeadingText
      v-motion
      :visible="{ opacity: 1, y: 0, scale: 1 }"
      :initial="{ opacity: 0, scale: 0.5 }"
      :delay="350"
      tag="span"
      class="mb-2 heading inline-flex justify-center font-bold uppercase text-blue font-display w-full text-6xl md:text-7xl lg:text-8xl 2xl:text-9xl z-20"
    >
      {{ props.title }}
    </HeadingText>
    <section
      v-motion
      :visible="{ opacity: 1, y: 0 }"
      :initial="{ opacity: 0, y: 0 }"
      :class="[theme]"
      :once="true"
      class=" w-full rounded-4xl body relative  bg-blue flex flex-col lg:flex-row lg:justify-end group interblock"
    >
      <img
        src="~/assets/icons/megaphone.svg"
        alt="Get in touch!"
        class="absolute w-1/3 h-auto -left-6 bottom-3 md:-left-6  group-hover:-rotate-3 duration-500"
      >
      <div class="max-w-screen-md w-full lg:w-3/5 lg:mr-[10%] py-12 md:py-24 xl:py-48 md:px-8 lg:px-12 px-6 text-white">
        <div
          v-motion
          class="subtitle font-display font-bold uppercase text-2xl md:text-4xl lg:text-5xl xl:text-6xl"
          :visible="{ opacity: 1, rotate: 0, y: 0, x: 0, scale: 1 }"
          :initial="{ opacity: 0, rotate: 5, x: 20, scale: 0.5 }"
          :delay="100"
        >
          {{ props.subtitle }}
        </div>

        <PageButtons
          v-if="buttons?.length"
          :buttons="expand?.buttons"
          class="pt-3 md:pt-6 lg:pt-12"
          :background="theme"
        />

        <div
          v-motion
          class="text text-lg lg:text-xl  paragraph"
          :visible="{ opacity: 1, rotate: 0, y: 0, x: 0, scale: 1 }"
          :initial="{ opacity: 0, rotate: 5, y: 20, x: 30, scale: 0.5 }"
          :delay="400"
        >
          <section
            class="htmldescription"
            data-allow-mismatch
            v-html="props.description"
          />

          <UForm
            :state="msg"
            :schema="schema"
            class="space-y-4 pt-6 md:pt-12 w-full lg:flex-1 duration-300 relative"

            @submit="sendMessage"
          >
            <div
              v-if="status === 'success'"
              class="absolute inset-0 z-50 flex flex-col items-center justify-center uppercase font-display text-xl md:text-2xl lg:text-4xl font-black"
            >
              Message Sent!
            </div>
            <div
              class="flex flex-col space-y-4"
              :class="[status === 'success' || status === 'loading' ? 'opacity-10': '']"
            >
              <UFormGroup
                name="name"
                :ui="label"
              >
                <UInput
                  v-model="msg.name"
                  :ui="input"
                  placeholder="* Name"
                />
              </UFormGroup>
              <UFormGroup
                name="email"
                :ui="label"
              >
                <UInput
                  v-model="msg.email"
                  :ui="input"
                  placeholder="* Email"
                />
              </UFormGroup>
              <UFormGroup
                name="message"
                :ui="label"
              >
                <UTextarea
                  v-model="msg.message"
                  :ui="input"
                  placeholder="* Message"
                />
              </UFormGroup>

              <div class="flex justify-end">
                <UButton
                  :disabled="status !== 'idle'"
                  type="submit"
                  size="1.5xl"
                  color="pinkblue"
                >
                  {{ buttonMessage }}
                  <template #trailing>
                    <Icon
                      name="gbf:arrow-right"
                      class="w-9 h-9 text-yellow"
                    />
                  </template>
                </UButton>
              </div>
            </div>
          </UForm>
          <section
            class="htmldescription basic pt-6 lg:pt-12 break-words pb-64 lg:pb-32 text-2xl"
            data-allow-mismatch
            v-html="props.descriptionAdditional"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped lang="css">
.paragraph:deep(p) {
  @apply pb-4;
}
</style>
